import React, { useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  Chip,
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import { LoadingButton } from '@mui/lab';
import { Person as PersonIcon } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { Debt } from '../../types';
import { customerAccountAmountColor, formatCustomerAccountAmount } from '../../utils/customerAccountAmount';
import { errorMessage } from '../../utils/errorMessage';
import { useDebtMutations } from '../../hooks/useDebtMutations';

interface DeleteDebtDialogProps {
  open: boolean;
  onClose: () => void;
  debt: Debt | null;
  onSuccess: () => void;
}

const DeleteDebtDialog: React.FC<DeleteDebtDialogProps> = ({
  open,
  onClose,
  debt,
  onSuccess,
}) => {
  const { t } = useLanguage();
  const [error, setError] = useState<string | null>(null);
  const { deleteDebtMutation } = useDebtMutations();
  const isLoading = deleteDebtMutation.isPending;

  const handleDelete = async () => {
    if (!debt) return;

    try {
      setError(null);
      await deleteDebtMutation.mutateAsync(debt.id);
      
      onSuccess();
      onClose();
    } catch (error: unknown) {
      setError(errorMessage(error, t('debts.deleteError')));
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      onClose();
    }
  };

  if (!debt) return null;

  return (
    <AppDialog presentation="confirm" open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('debts.deleteDebt')}</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" gutterBottom>
          {t('debts.deleteConfirmation')}
        </Typography>

        {/* Debt Details */}
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          border: 1, 
          borderColor: 'divider', 
          borderRadius: 1,
          backgroundColor: 'background.default'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PersonIcon color="primary" />
            <Typography variant="subtitle2">
              {t('debts.customer')}: {debt.customerName}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2">{t('debts.amount')}:</Typography>
            <Typography variant="subtitle2" sx={{ color: customerAccountAmountColor(debt.amount) }}>
              {formatCustomerAccountAmount(debt.amount)}
            </Typography>
          </Box>

          {debt.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('debts.description')}: {debt.description}
            </Typography>
          )}

          <Box sx={{ mt: 1 }}>
            <Chip 
              label={`${t('debts.createdBy')}: ${debt.createdByUsername}`}
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>

        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('debts.deleteWarning')}
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isLoading}
        >
          {t('common.cancel')}
        </Button>
        <LoadingButton
          onClick={handleDelete}
          loading={isLoading}
          variant="contained"
          color="error"
        >
          {t('common.delete')}
        </LoadingButton>
      </DialogActions>
    </AppDialog>
  );
};

export default DeleteDebtDialog;
